/**
 * Monta o payload de exportação (scheduler) igual ao ExportTableCSV/CadastroToolbar
 * de cada tela REL*.vue, para RelatoriosExtenso gerar o mesmo arquivo.
 */
import apiController from '@/utils/apiController';
import { fetchTabelaFrontHeaders } from '@/utils/tabelasFrontHeaders';
import {
  buildRelatorioExtensoRequestParams,
  getRelatorioExtensoTableVariant,
} from '@/utils/relatoriosExtensoCatalog';
import {
  eqFilter,
  inFilter,
  mergePostFilters,
  notNullFilter,
} from '@/utils/csvExportPostFilters';

/** @typedef {import('./relatoriosExtensoCatalog').RelatorioExtensoEntry} RelatorioExtensoEntry */

/**
 * @typedef {object} RelatorioExportContext
 * @property {string|null} dtiSql
 * @property {string|null} dtfSql
 * @property {number|null} empresa
 * @property {number[]} linhas
 * @property {number[]} veiculos
 * @property {number[]} unidades
 * @property {string} grupo
 * @property {number|null} cerca
 * @property {string} format
 * @property {string} token
 * @property {object|null} user
 * @property {object[]} linhasCatalog
 * @property {object|null} setup
 * @property {object|null} config
 * @property {string|null} [intervalo]
 * @property {string|null} [tableVariant]
 * @property {(key: string, ...args: unknown[]) => string} t
 * @property {RelatorioExtensoEntry} entry
 */

export function isHeaderSelectedForExport(header) {
  if (!header) return true;
  if (header.to_export !== undefined && header.to_export !== null) {
    return Number(header.to_export) !== 0;
  }
  return header.to_show === 1 || header.to_show === true || header.show !== false;
}

/** Formata dti/dtf SQL (ISO) para subtítulo legível dd/MM/yyyy HH:mm:ss. */
export function formatExportSubtitle(dtiSql, dtfSql) {
  const fmt = (raw) => {
    if (raw == null || raw === '') return '';
    const m = String(raw).match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}):(\d{2}))?/
    );
    if (!m) return String(raw);
    const time = m[4] != null ? ` ${m[4]}:${m[5]}:${m[6]}` : '';
    return `${m[3]}/${m[2]}/${m[1]}${time}`;
  };
  const a = fmt(dtiSql);
  const b = fmt(dtfSql);
  // Hífen ASCII: o worker PDF no Windows pode cair em Helvetica (Latin-1),
  // que rejeita o em-dash "—" (U+2014) com FPDFUnicodeEncodingException.
  if (a && b) return `${a} - ${b}`;
  return a || b || '';
}

function colT(t, titleKey, key) {
  return { key, title: t(titleKey) };
}

export function headersToExportColumns(headers) {
  return (headers || [])
    .filter(
      (h) =>
        h &&
        h.value &&
        h.value !== 'actions' &&
        h.value !== 'data-table-expand' &&
        h.align !== ' d-none'
    )
    .filter(isHeaderSelectedForExport)
    .map((h) => ({
      key: h.value,
      title: h.text || h.value,
    }));
}

export async function resolveTabelaFrontExportColumns(codTabela, token) {
  try {
    const headers = await fetchTabelaFrontHeaders(codTabela, token);
    const columns = headersToExportColumns(headers);
    return columns.length ? columns : null;
  } catch (_err) {
    return null;
  }
}

function acessoQrCodeColumns(t) {
  const col = (titleKey, key) => ({
    key,
    title: t(titleKey),
  });
  return [
    col('qrcodeAccessReport.headers.registration', 'matricula'),
    col('qrcodeAccessReport.headers.name', 'nome_completo'),
    col('qrcodeAccessReport.headers.unit', 'cod_unidade'),
    col('qrcodeAccessReport.headers.group', 'nome_grupo'),
    col('qrcodeAccessReport.headers.shift', 'cod_turno'),
    col('qrcodeAccessReport.headers.accessed', 'acessado'),
    col('qrcodeAccessReport.headers.downloaded', 'baixado'),
    col('qrcodeAccessReport.headers.firstAccess', 'primeiro_acesso_qrcode_extenso'),
    col('qrcodeAccessReport.headers.lastAccess', 'ultimo_acesso_qrcode_extenso'),
    col('qrcodeAccessReport.headers.firstDownload', 'primeiro_download_qrcode_extenso'),
    col('qrcodeAccessReport.headers.lastDownload', 'ultimo_download_qrcode_extenso'),
  ];
}

/** Colunas do relatório de cumprimento de rota. */
function cumprimentoRotaColumns(t) {
  return [
    colT(t, 'monitor.logbook.line', 'cod_linha'),
    colT(t, 'fields.vehicle', 'numero_ordem'),
    colT(t, 'monitor.trip.departure', 'dt_inicio_extenso'),
    colT(t, 'monitor.trip.arrival', 'dt_fim_extenso'),
    colT(t, 'operation.direction', 'descricao_sentido'),
    colT(t, 'operation.driverRegistration', 'matricula_motorista'),
    colT(t, 'operation.driverName', 'nome_motorista'),
    colT(t, 'operation.collectorRegistration', 'matricula_cobrador'),
    colT(t, 'operation.collectorName', 'nome_cobrador'),
    colT(t, 'routeCompliance.headers.temperature', 'temp_media'),
    colT(t, 'routeCompliance.headers.weather', 'condicao_climatica'),
    colT(t, 'routeCompliance.headers.onRoutePercentage', 'p_dentro_rota'),
  ];
}

function atrasoViagensColumns(t) {
  return [
    colT(t, 'relTripDelay.headers.lineCode', 'cod_linha'),
    colT(t, 'relTripDelay.headers.lineName', 'nome_linha'),
    colT(t, 'relTripDelay.headers.vehicle', 'numero_ordem'),
    colT(t, 'relTripDelay.headers.conductor', 'nome_motorista'),
    colT(t, 'relTripDelay.headers.registration', 'matricula_motorista'),
    colT(t, 'relTripDelay.headers.direction', 'descricao_sentido'),
    colT(t, 'relTripDelay.headers.plannedDeparture', 'horario_saida_prevista_extenso'),
    colT(t, 'relTripDelay.headers.actualDeparture', 'horario_saida_realizada_extenso'),
    colT(t, 'relTripDelay.headers.departureDelay', 'delay_saida_min'),
    colT(t, 'relTripDelay.headers.plannedArrival', 'horario_chegada_prevista_extenso'),
    colT(t, 'relTripDelay.headers.actualArrival', 'horario_chegada_realizada_extenso'),
    colT(t, 'relTripDelay.headers.arrivalDelay', 'delay_chegada_min'),
    colT(t, 'relTripDelay.headers.tripDelay', 'delay_trip_min'),
  ];
}

function diarioBordoColumns(t) {
  return [
    colT(t, 'relatoriosExtenso.headers.entryType', 'descricao_tipo_entrada'),
    colT(t, 'relatoriosExtenso.headers.reason', 'descricao_motivo'),
    colT(t, 'monitor.logbook.line', 'linhas'),
    colT(t, 'fields.vehicle', 'veiculos'),
    colT(t, 'datetime.date', 'dt_entrada'),
    colT(t, 'relatoriosExtenso.headers.createdBy', 'usuario_inclusao'),
    colT(t, 'relatoriosExtenso.headers.notes', 'observacoes'),
  ];
}

function usuariosPassageirosColumns(t) {
  return [
    colT(t, 'passengersUsers.headers.registration', 'matricula'),
    colT(t, 'passengersUsers.headers.name', 'nome_completo'),
    colT(t, 'passengersUsers.headers.email', 'email'),
    colT(t, 'passengersUsers.headers.login', 'login'),
    colT(t, 'passengersUsers.headers.initialPassword', 'senha_inicial'),
    colT(t, 'passengersUsers.headers.unit', 'cod_unidade'),
    colT(t, 'passengersUsers.headers.group', 'nome_grupo'),
    colT(t, 'passengersUsers.headers.inclusionDate', 'dt_inclusao_extenso'),
  ];
}

function consumoApiExternaColumns(t) {
  return [
    colT(t, 'relConsumoApiExterna.headers.operator', 'operador_api_externa'),
    colT(t, 'relConsumoApiExterna.headers.service', 'servico_api_externa'),
    colT(t, 'relConsumoApiExterna.headers.currentMonthUsage', 'quantidade_mes_vigente'),
    colT(t, 'relConsumoApiExterna.headers.periodUsage', 'quantidade_periodo'),
    colT(t, 'relConsumoApiExterna.headers.monthlyLimit', 'limite_chamadas'),
    colT(t, 'relConsumoApiExterna.headers.nextRefresh', 'will_refresh_at'),
  ];
}

function manutencaoMetricasColumns(t, variantId) {
  const columns = [
    colT(t, 'relMaintenence.headerEquipment.code', 'cod_equipamento'),
    colT(t, 'relMaintenence.headerEquipment.vehicle', 'numero_ordem'),
    colT(t, 'relMaintenence.headerEquipment.model', 'modelo'),
    colT(t, 'relMaintenence.headerEquipment.type', 'nome_tipo_equipamento'),
    colT(t, 'relMaintenence.headerEquipment.maintenance', 'manutencao'),
    colT(t, 'relMaintenence.headerEquipment.percentageSend', 'p_envio_total'),
    colT(t, 'relMaintenence.headerEquipment.percentageValid', 'p_validas'),
    colT(t, 'relMaintenence.headerEquipment.percentageOutOfOrder', 'p_posicoes_fora_ordem'),
    colT(t, 'relMaintenence.headerEquipment.percentageDelay', 'p_delay'),
    colT(t, 'relMaintenence.headerEquipment.lastValidPosition', 'dt_ultima_posicao'),
  ];
  if (variantId === 'validadores_rastreadores_bilhetagem') {
    columns.push(colT(t, 'relMaintenence.headerEquipment.lastBill', 'dt_ultima_bilhetagem'));
  }
  columns.push(
    colT(t, 'relMaintenence.headerEquipment.lastGuide', 'dt_ultima_guia'),
    colT(t, 'relMaintenence.headerEquipment.lastPosition', 'ultima_posicao'),
  );
  if (variantId !== 'validadores_rastreadores_bilhetagem') {
    columns.push(
      colT(t, 'relMaintenence.headerEquipment.isDriving', 'is_driving_extenso'),
      colT(t, 'relMaintenence.headerEquipment.statusGeotabReal', 'status_geotab_extenso'),
    );
  }
  return columns;
}

function manutencaoComunicacaoColumns(t) {
  return [
    colT(t, 'relMaintenanceHistory.otherEquipment.communicationStatus', 'status_extenso'),
    colT(t, 'relMaintenanceHistory.otherEquipment.equipment', 'cod_equipamento'),
    colT(t, 'relMaintenanceHistory.otherEquipment.name', 'nome_equipamento'),
    colT(t, 'relMaintenanceHistory.otherEquipment.type', 'nome_tipo_equipamento'),
    colT(t, 'fields.vehicle', 'numero_ordem'),
    colT(t, 'relMaintenanceHistory.otherEquipment.lastUpdate', 'dt_ultima_atualizacao_comunicacao_extenso'),
  ];
}

function passageirosFaixasColumns(t) {
  return [
    colT(t, 'relSubsidy.headersTrip.start', 'inicio_extenso'),
    colT(t, 'relSubsidy.headersTrip.end', 'fim_extenso'),
    colT(t, 'fields.vehicle', 'numero_ordem'),
    colT(t, 'monitor.logbook.line', 'cod_linha'),
    colT(t, 'relTripPassangers.labelpassangerHour.paid', 'passageiros_pagantes'),
    colT(t, 'relTripPassangers.labelpassangerHour.nonPaid', 'passageiros_gratuidades'),
    colT(t, 'relTripPassangers.labelpassangerHour.vt', 'passageiros_vt'),
    colT(t, 'relTripPassangers.labelpassangerHour.others', 'passageiros_outros'),
    colT(t, 'relTripPassangers.labelpassangerHour.total', 'passageiros_totais'),
  ];
}

function passageirosViagemTripColumns(t) {
  return [
    colT(t, 'relTripPassangers.headers.line', 'cod_linha'),
    colT(t, 'relTripPassangers.headers.vehicle', 'numero_ordem_veiculo_realizado'),
    colT(t, 'relTripPassangers.headers.start', 'saida_realizada_extenso'),
    colT(t, 'relTripPassangers.headers.end', 'chegada_realizada_extenso'),
    colT(t, 'relTripPassangers.headers.direction', 'descricao_sentido'),
    colT(t, 'relTripPassangers.headers.temperature', 'temp_media'),
    colT(t, 'relTripPassangers.headers.climate', 'condicao_climatica'),
    colT(t, 'relTripPassangers.headers.paid', 'passageiros_pagantes'),
    colT(t, 'relTripPassangers.headers.nonPaid', 'passageiros_gratuidades'),
    colT(t, 'relTripPassangers.headers.vt', 'passageiros_vt'),
    colT(t, 'relTripPassangers.headers.others', 'passageiros_outros'),
    colT(t, 'relTripPassangers.headers.total', 'passageiros_totais'),
  ];
}

function comboioColumns(t) {
  return [
    colT(t, 'guide.headers.line', 'cod_linha'),
    colT(t, 'operation.direction', 'descricao_sentido'),
    colT(t, 'fields.vehicle', 'numero_ordem'),
    colT(t, 'convoyReport.startDate', 'dt_inicio_extenso'),
    colT(t, 'convoyReport.endDate', 'dt_fim_extenso'),
  ];
}

function historicoAlteracoesRotasColumns(t) {
  return [
    colT(t, 'routeChangeHistory.headers.destination', 'nome_destino'),
    colT(t, 'routeChangeHistory.headers.routeCode', 'cod_rota'),
    colT(t, 'routeChangeHistory.headers.routeName', 'nome_rota'),
    colT(t, 'routeChangeHistory.headers.routingMode', 'modo_roteirizacao_extenso'),
    colT(t, 'routeChangeHistory.headers.costConstancy', 'constancia_custo_extenso'),
    colT(t, 'routeChangeHistory.headers.kmOriginal', 'km_original'),
    colT(t, 'routeChangeHistory.headers.kmAtual', 'km_atual'),
    colT(t, 'routeChangeHistory.headers.deltaKm', 'delta_km'),
    colT(t, 'routeChangeHistory.headers.custoOriginal', 'custo_original'),
    colT(t, 'routeChangeHistory.headers.custoAtual', 'custo_atual'),
    colT(t, 'routeChangeHistory.headers.deltaCusto', 'delta_custo'),
    colT(t, 'routeChangeHistory.headers.pctCusto', 'pct_custo'),
    colT(t, 'routeChangeHistory.headers.lastOrigin', 'ultima_origem'),
    colT(t, 'routeChangeHistory.headers.lastEvent', 'dt_ultimo_evento_extenso'),
  ];
}

function historicoEquipamentosColumns(t) {
  return [
    colT(t, 'fields.vehicle', 'numero_ordem'),
    colT(t, 'relMaintenanceHistory.otherEquipment.type', 'nome_tipo_equipamento'),
    colT(t, 'relMaintenence.headerEquipment.code', 'cod_equipamento'),
    colT(t, 'datetime.date', 'dia_extenso'),
    colT(t, 'relMaintenanceHistory.percentageSend', 'p_envio'),
    colT(t, 'relMaintenanceHistory.percentageValid', 'p_validas'),
    colT(t, 'relMaintenanceHistory.bill', 'total_bilhetagens'),
    colT(t, 'relMaintenence.headerEquipment.maintenance', 'manutencao'),
  ];
}

function historicoRoteirizacaoColumns(t) {
  return [
    colT(t, 'routingHistoryTimeline.headers.destination', 'nome_destino'),
    colT(t, 'routingHistoryTimeline.headers.startedAt', 'dt_inicio_extenso'),
    colT(t, 'routingHistoryTimeline.headers.endedAt', 'dt_fim_extenso'),
    colT(t, 'routingHistoryTimeline.headers.eventCount', 'qtd_eventos'),
    colT(t, 'routingHistoryTimeline.headers.lastEventType', 'ultimo_tipo_evento_extenso'),
    colT(t, 'routingHistoryTimeline.headers.lastVersion', 'ultima_versao'),
    colT(t, 'routingHistoryTimeline.headers.km', 'km_total'),
    colT(t, 'routingHistoryTimeline.headers.cost', 'custo_total'),
    colT(t, 'routingHistoryTimeline.headers.drivers', 'qtd_motoristas'),
    colT(t, 'routingHistoryTimeline.headers.driversCost', 'custo_motoristas_total'),
    colT(t, 'routingHistoryTimeline.headers.routes', 'qtd_rotas_operacionais'),
  ];
}

function historicoVeiculoGuiasColumns(t) {
  return [
    colT(t, 'RELHistoricoVeiculo.headerGuides.guide', 'guia_original'),
    colT(t, 'RELHistoricoVeiculo.headerGuides.line', 'cod_linha'),
    colT(t, 'RELHistoricoVeiculo.headerGuides.vehicle', 'cod_veiculo'),
    colT(t, 'RELHistoricoVeiculo.headerGuides.driver', 'matricula_motorista'),
    colT(t, 'RELHistoricoVeiculo.headerGuides.driverName', 'nome_motorista'),
    colT(t, 'RELHistoricoVeiculo.headerGuides.collector', 'matricula_cobrador'),
    colT(t, 'RELHistoricoVeiculo.headerGuides.collectorName', 'nome_cobrador'),
    colT(t, 'RELHistoricoVeiculo.headerGuides.picked', 'dt_pegada_extenso'),
    colT(t, 'RELHistoricoVeiculo.headerGuides.departure', 'dt_largada_extenso'),
    colT(t, 'RELHistoricoVeiculo.headerGuides.shift', 'turno'),
    colT(t, 'RELHistoricoVeiculo.headerGuides.escalation', 'escalonamento'),
    colT(t, 'RELHistoricoVeiculo.headerGuides.opening', 'dt_abertura_erp_extenso'),
    colT(t, 'RELHistoricoVeiculo.headerGuides.closing', 'dt_fechamento_erp_extenso'),
  ];
}

function historicoVeiculoViagensColumns(t) {
  return [
    colT(t, 'RELHistoricoVeiculo.headerViagens.line', 'cod_linha'),
    colT(t, 'RELHistoricoVeiculo.headerViagens.guide', 'numero_guia_realizada'),
    colT(t, 'RELHistoricoVeiculo.headerViagens.scheduledDeparture', 'horario_saida_prevista_extenso'),
    colT(t, 'RELHistoricoVeiculo.headerViagens.actualDeparture', 'horario_saida_realizada_extenso'),
    colT(t, 'RELHistoricoVeiculo.headerViagens.scheduledArrival', 'horario_chegada_prevista_extenso'),
    colT(t, 'RELHistoricoVeiculo.headerViagens.actualArrival', 'horario_chegada_realizada_extenso'),
    colT(t, 'RELHistoricoVeiculo.headerViagens.direction', 'descricao_sentido'),
    colT(t, 'RELHistoricoVeiculo.headerViagens.shift', 'descricao_turno'),
    colT(t, 'RELHistoricoVeiculo.headerViagens.passengers', 'passageiros_totais'),
    colT(t, 'RELHistoricoVeiculo.headerViagens.vehicleEscalation', 'escalonamento_veiculo'),
    colT(t, 'RELHistoricoVeiculo.headerViagens.tripEscalation', 'escalonamento_viagem'),
    colT(t, 'RELHistoricoVeiculo.headerViagens.opening', 'abertura'),
    colT(t, 'RELHistoricoVeiculo.headerViagens.closing', 'fechamento'),
    colT(t, 'RELHistoricoVeiculo.headerViagens.dtOpening', 'dt_abertura_manual_extenso'),
    colT(t, 'RELHistoricoVeiculo.headerViagens.dtClosing', 'dt_fechamento_manual_extenso'),
  ];
}

function historicoVeiculoBilhetagensColumns(t) {
  return [
    colT(t, 'RELHistoricoVeiculo.headerBilhetagens.dateTime', 'dt_posicao_extenso'),
    colT(t, 'RELHistoricoVeiculo.headerBilhetagens.application', 'descricao_aplicacao'),
    colT(t, 'RELHistoricoVeiculo.headerBilhetagens.applicationType', 'descricao_tipo_aplicacao'),
    colT(t, 'RELHistoricoVeiculo.headerBilhetagens.issuer', 'descricao_emissor'),
    colT(t, 'RELHistoricoVeiculo.headerBilhetagens.paidValue', 'valor_pago'),
  ];
}

function historicoVeiculoNotificacoesColumns(t) {
  return [
    colT(t, 'RELHistoricoVeiculo.headerNotifications.dateTime', 'dt_notificacao_extenso'),
    colT(t, 'RELHistoricoVeiculo.headerNotifications.notificationType', 'descricao_tipo_notificacao'),
    colT(t, 'RELHistoricoVeiculo.headerNotifications.line', 'cod_linha'),
    colT(t, 'RELHistoricoVeiculo.headerNotifications.treated', 'reset_extenso'),
  ];
}

function historicoVeiculoColumnsByVariant(t, variantId) {
  switch (variantId) {
    case 'guias':
      return historicoVeiculoGuiasColumns(t);
    case 'bilhetagens':
      return historicoVeiculoBilhetagensColumns(t);
    case 'notificacoes':
      return historicoVeiculoNotificacoesColumns(t);
    case 'viagens':
    default:
      return historicoVeiculoViagensColumns(t);
  }
}

function historicoVeiculoRequestParams(variantId, ctx) {
  const idveiculo = ctx.veiculos.length ? ctx.veiculos[0] : null;
  const dti = ctx.dtiSql;
  const dtf = ctx.dtfSql;
  switch (variantId) {
    case 'bilhetagens':
      return {
        id: idveiculo != null ? [idveiculo] : [],
        dti,
        dtf,
      };
    case 'notificacoes':
      return { dti, dtf, idveiculo };
    case 'guias':
    case 'viagens':
    default:
      return { idveiculo, dti, dtf };
  }
}

function kmTempoRealColumns(t) {
  return [
    colT(t, 'relStatusVehicle.headers.codeVehicle', 'cod_veiculo'),
    colT(t, 'fields.vehicle', 'numero_ordem'),
    colT(t, 'realTimeKmReport.headers.year', 'ano_modelo'),
    colT(t, 'relVehicleKm.headersVeiculosKm.equipment', 'cod_equipamento'),
    colT(t, 'realTimeKmReport.headers.virtualOdometer', 'hodometro_virtual'),
    colT(t, 'realTimeKmReport.headers.virtualReadingDate', 'dt_hodometro_virtual_extenso'),
    colT(t, 'realTimeKmReport.headers.canbusOdometer', 'hodometro_canbus'),
    colT(t, 'realTimeKmReport.headers.canbusReadingDate', 'dt_hodometro_canbus_extenso'),
    colT(t, 'realTimeKmReport.headers.canbusVirtualDifference', 'diff_canbus_virtual'),
    colT(t, 'realTimeKmReport.headers.canbusVirtualDifferencePercent', 'perc_diff_canbus_virtual'),
    colT(t, 'realTimeKmReport.headers.lastPositionDate', 'dt_ultima_posicao_extenso'),
    colT(t, 'realTimeKmReport.headers.canbusReadingDelay', 'atraso_leitura_canbus'),
    colT(t, 'relatoriosExtenso.headers.status', 'status_avl_extenso'),
  ];
}

function previstoRealizadoColumns(t) {
  return [
    colT(t, 'datetime.date', 'dt_referencia_extenso'),
    colT(t, 'monitor.logbook.line', 'cod_linha'),
    colT(t, 'relatoriosExtenso.headers.guide', 'numero_guia'),
    colT(t, 'fields.vehicle', 'numero_ordem'),
    colT(t, 'crudQuadros.headers.trips.departureTime', 'saida_prevista_extenso'),
    colT(t, 'relatoriosExtenso.headers.actualDeparture', 'saida_realizada_extenso'),
    colT(t, 'operation.direction', 'descricao_sentido'),
    colT(t, 'crudQuadros.headers.trips.shift', 'descricao_turno'),
    colT(t, 'fechamentoDia.headers.tripClassification', 'descricao_classificacao'),
    colT(t, 'relatoriosExtenso.headers.plannedArrival', 'chegada_prevista_extenso'),
    colT(t, 'relatoriosExtenso.headers.actualArrival', 'chegada_realizada_extenso'),
    colT(t, 'operation.driverName', 'nome_motorista'),
    colT(t, 'operation.driverRegistration', 'matricula_motorista'),
    colT(t, 'relatoriosExtenso.headers.status', 'status_extenso'),
    colT(t, 'relatoriosExtenso.headers.occupancyRate', 'taxa_ocupacao_embarques_validos'),
    { key: 'km_total', title: 'KM' },
    { key: 'diesel_total', title: 'Diesel' },
    { key: 'total_embarques_validos', title: t('nav.reports.boarding') },
  ];
}

function historicoEnvioQrCodeColumns(t) {
  return [
    colT(t, 'qrcodeSendHistoryReport.headers.sendDate', 'dt_envio_extenso'),
    colT(t, 'qrcodeSendHistoryReport.headers.registration', 'matricula'),
    colT(t, 'qrcodeSendHistoryReport.headers.name', 'nome_completo'),
    colT(t, 'qrcodeSendHistoryReport.headers.email', 'email'),
    colT(t, 'qrcodeSendHistoryReport.headers.unit', 'cod_unidade'),
    colT(t, 'qrcodeSendHistoryReport.headers.group', 'nome_grupo'),
    colT(t, 'qrcodeSendHistoryReport.headers.shift', 'cod_turno'),
    colT(t, 'qrcodeSendHistoryReport.headers.sentBy', 'nome_usuario_envio'),
    colT(t, 'qrcodeSendHistoryReport.headers.status', 'status_extenso'),
    colT(t, 'qrcodeSendHistoryReport.headers.message', 'mensagem'),
  ];
}

function atrasoCercasColumns(t) {
  return [
    colT(t, 'datetime.date', 'dt_passou_na_cerca_extenso'),
    colT(t, 'monitor.logbook.line', 'cod_linha'),
    colT(t, 'fields.vehicle', 'cod_veiculo'),
    colT(t, 'delayByGeofence.driverRegistration', 'matricula_motorista'),
    colT(t, 'operation.driverName', 'nome_motorista'),
    colT(t, 'geofence.name', 'nome_cerca'),
    colT(t, 'relatoriosExtenso.headers.plannedArrival', 'dt_chegada_prevista_extenso'),
    colT(t, 'delayByGeofence.delayMinutes', 'atraso_minutos'),
    colT(t, 'delayByGeofence.occupancy', 'ocupacao'),
    colT(t, 'relatoriosExtenso.headers.occupancyRate', 'taxa_ocupacao'),
  ];
}

function controladoresColumns(t) {
  return [
    colT(t, 'datetime.date', 'dt_referencia_extenso'),
    colT(t, 'monitor.logbook.line', 'cod_linha'),
    colT(t, 'fields.vehicle', 'numero_ordem'),
    colT(t, 'relatoriosExtenso.headers.guide', 'numero_guia'),
    colT(t, 'operation.direction', 'descricao_sentido'),
    colT(t, 'crudQuadros.headers.trips.shift', 'descricao_turno'),
    colT(t, 'monitor.trip.departure', 'horario_saida_realizada_extenso'),
    // typo histórico da API: horario_chegada_relizada_extenso
    colT(t, 'monitor.trip.arrival', 'horario_chegada_relizada_extenso'),
    colT(t, 'relatoriosExtenso.headers.startFence', 'nome_cerca_inicio'),
    colT(t, 'relatoriosExtenso.headers.endFence', 'nome_cerca_fim'),
    colT(t, 'relatoriosExtenso.headers.passengers', 'passageiros'),
  ];
}

function notificacoesColumns(t) {
  return [
    colT(t, 'relNotification.header.date', 'dt_notificacao_extenso'),
    colT(t, 'relNotification.header.code', 'numero_ordem'),
    colT(t, 'relNotification.header.equipment', 'nome_equipamento'),
    colT(t, 'relNotification.header.type', 'descricao_tipo_notificacao'),
    colT(t, 'relNotification.header.area', 'nome_area'),
    colT(t, 'relNotification.header.geofence', 'nome_cerca'),
    colT(t, 'relNotification.header.line', 'nome_linha'),
    colT(t, 'relNotification.header.treated', 'reset_extenso'),
    colT(t, 'relNotification.header.address', 'cidade'),
    colT(t, 'relNotification.header.billOutArea', 'bil_fora_area_extenso'),
  ];
}

function tempoDePlacaColumns(t) {
  return [
    colT(t, 'datetime.date', 'dt_referencia_extenso'),
    colT(t, 'monitor.logbook.line', 'cod_linha'),
    colT(t, 'fields.vehicle', 'numero_ordem'),
    colT(t, 'crudQuadros.headers.trips.departureTime', 'saida_prevista_extenso'),
    colT(t, 'relatoriosExtenso.headers.actualDeparture', 'saida_realizada_extenso'),
    colT(t, 'operation.direction', 'descricao_sentido'),
    colT(t, 'crudQuadros.headers.trips.shift', 'descricao_turno'),
    colT(t, 'relatoriosExtenso.headers.plannedArrival', 'chegada_prevista_extenso'),
    colT(t, 'relatoriosExtenso.headers.actualArrival', 'chegada_realizada_extenso'),
    colT(t, 'operation.driverName', 'nome_motorista'),
    colT(t, 'relatoriosExtenso.headers.dwellTime', 'tempo_de_placa'),
    colT(t, 'relatoriosExtenso.headers.status', 'status_extenso'),
  ];
}

function tempoTrechoColumns(t) {
  return [
    colT(t, 'monitor.logbook.line', 'cod_linha'),
    colT(t, 'fields.vehicle', 'numero_ordem'),
    colT(t, 'relatoriosExtenso.headers.startFence', 'cerca_inicio'),
    colT(t, 'relatoriosExtenso.headers.endFence', 'cerca_fim'),
    colT(t, 'operation.direction', 'descricao_sentido'),
    colT(t, 'operation.driverName', 'nome_motorista'),
    colT(t, 'monitor.trip.departure', 'saida_cerca_extenso'),
    colT(t, 'monitor.trip.arrival', 'entrada_cerca_extenso'),
    colT(t, 'relatoriosExtenso.headers.stretchTime', 'tempo_trecho'),
  ];
}

function tempoPlacaServicoColumns(t) {
  return [
    colT(t, 'employee.headers.registration', 'matricula'),
    colT(t, 'qrcodeAccessReport.headers.name', 'nome'),
    colT(t, 'fields.vehicle', 'numero_ordem'),
    colT(t, 'relatoriosExtenso.headers.serviceStart', 'inicio_servico_extenso'),
    colT(t, 'relatoriosExtenso.headers.serviceEnd', 'fim_servico_extenso'),
    colT(t, 'relatoriosExtenso.headers.dwellTimeTotal', 'tempo_placa_total'),
    colT(t, 'relatoriosExtenso.headers.serviceTime', 'tempo_servico'),
  ];
}

function statusViagensColumns(t) {
  return [
    colT(t, 'relStatusTrip.headers.line', 'cod_linha'),
    colT(t, 'relStatusTrip.headers.date', 'data_extenso'),
    colT(t, 'relStatusTrip.headers.total', 'total'),
    colT(t, 'relStatusTrip.headers.completed', 'completas'),
    colT(t, 'relStatusTrip.headers.completedPercent', 'p_completas'),
    colT(t, 'relStatusTrip.headers.noInitial', 'sem_inicio'),
    colT(t, 'relStatusTrip.headers.noInitialPercent', 'p_sem_inicio'),
    colT(t, 'relStatusTrip.headers.noEnd', 'sem_fim'),
    colT(t, 'relStatusTrip.headers.noEndPercent', 'p_sem_fim'),
    colT(t, 'relStatusTrip.headers.manuals', 'manuais'),
    colT(t, 'relStatusTrip.headers.manualsPercent', 'p_manuais'),
    colT(t, 'relStatusTrip.headers.automatics', 'automaticas'),
    colT(t, 'relStatusTrip.headers.automaticsPercent', 'p_automaticas'),
  ];
}

function embarquesTrechoColumns(t) {
  return [
    colT(t, 'monitor.logbook.line', 'cod_linha'),
    colT(t, 'fields.vehicle', 'numero_ordem'),
    colT(t, 'relatoriosExtenso.headers.startFence', 'cerca_inicio'),
    colT(t, 'relatoriosExtenso.headers.endFence', 'cerca_fim'),
    colT(t, 'operation.direction', 'descricao_sentido'),
    colT(t, 'operation.driverName', 'nome_motorista'),
    colT(t, 'monitor.trip.departure', 'saida_cerca_extenso'),
    colT(t, 'monitor.trip.arrival', 'entrada_cerca_extenso'),
    colT(t, 'relatoriosExtenso.headers.stretchTime', 'tempo_trecho'),
    colT(t, 'relatoriosExtenso.headers.paying', 'passageiros_pagantes'),
    colT(t, 'relatoriosExtenso.headers.free', 'passageiros_gratuidades'),
    colT(t, 'relatoriosExtenso.headers.vt', 'passageiros_vt'),
    colT(t, 'relatoriosExtenso.headers.passengers', 'passageiros_totais'),
  ];
}

/** Colunas do relatório de embarques (evita autoColumns com ids/lat/lng/ISO da API). */
function embarquesColumns(t) {
  const col = (titleKey, key) => ({
    key,
    title: t(titleKey),
  });
  return [
    col('datetime.date', 'data_embarque'),
    col('datetime.time', 'hora_embarque'),
    col('passengers.personal.registrationCode', 'matricula'),
    col('qrcodeAccessReport.headers.name', 'nome_completo'),
    col('passengers.personal.leader', 'lider'),
    col('relBoarding.parada', 'cod_cerca'),
    col('monitor.logbook.line', 'cod_linha_veiculo'),
    col('relBoarding.header.group', 'nome_grupo'),
    col('fields.vehicle', 'numero_ordem'),
    col('crudQuadros.headers.trips.departureTime', 'horario_saida_viagem_extenso'),
    col('crudQuadros.headers.trips.arrivalTime', 'horario_chegada_viagem_extenso'),
    col('crudQuadros.headers.trips.direction', 'descricao_sentido_viagem'),
    col('crudQuadros.headers.trips.shift', 'descricao_turno_viagem'),
    col('fechamentoDia.headers.tripClassification', 'descricao_classificacao_viagem'),
    col('vehicleAuthorization.headers.authorized', 'autorizado_extenso'),
    col('relBoarding.header.alerts', 'sem_posicoes_janela'),
  ];
}

/** Colunas do relatório de velocidade (posições brutas da API — não o agregado da tela). */
function velocidadeColumns(t) {
  const col = (titleKey, key) => ({
    key,
    title: t(titleKey),
  });
  return [
    col('speedReport.dateTime', 'dt_posicao_extenso'),
    col('tripOperation.vehicle', 'numero_ordem'),
    col('operation.notificationHeaders.line', 'cod_linha'),
    col('employee.headers.registration', 'id_funcionario_motorista'),
    col('tripOperation.driver', 'nome_motorista'),
    col('geofence.speed', 'velocidade'),
  ];
}

/** Colunas do relatório de eventos Hikvision (evita autoColumns com ids/lat/lng da API). */
function eventosHikvisionColumns(t) {
  const col = (titleKey, key) => ({
    key,
    title: t(titleKey),
  });
  return [
    col('datetime.date', 'dt_evento_extenso'),
    col('passengers.personal.registrationCode', 'matricula'),
    col('qrcodeAccessReport.headers.name', 'nome_completo'),
    col('passengers.personal.cardNumber', 'numero_cartao'),
    col('passengers.personal.shifts', 'cod_turno'),
    col('geofence.name', 'nome_cerca'),
    col('monitor.logbook.line', 'cod_linha'),
    col('relBoarding.header.group', 'nome_grupo'),
    col('fields.vehicle', 'numero_ordem'),
    col('vehicleAuthorization.headers.authorized', 'autorizado_extenso'),
  ];
}

/** Colunas do relatório de operação (PDF/CSV enxuto — sem mês/ocupação derivada/flags vazios). */
function operacaoColumns(t) {
  const col = (titleKey, key) => ({
    key,
    title: t(titleKey),
  });
  return [
    col('relOperacao.headers.date', 'date'),
    col('relOperacao.headers.site', 'site'),
    col('relOperacao.headers.line', 'line'),
    col('relOperacao.headers.shift', 'shift'),
    col('relOperacao.headers.direction', 'direction'),
    col('relOperacao.headers.vehicleNumber', 'vehicleNumber'),
    col('relOperacao.headers.driver', 'driver'),
    col('relOperacao.headers.occupancy', 'occupancy'),
    col('relOperacao.headers.plannedArrivalTime', 'plannedArrivalTime'),
    col('relOperacao.headers.actualArrivalTime', 'actualArrivalTime'),
    col('relOperacao.headers.operatorDelay', 'operatorDelay'),
    col('relOperacao.headers.tripDelay', 'tripDelay'),
    col('relOperacao.headers.kmTraveled', 'kmTraveled'),
    col('relOperacao.headers.incidenceType', 'incidenceType'),
  ];
}

/** Colunas da taxa de ocupação por turno (evita autoColumns com id_linha/id_turno/id_cliente). */
function ocupacaoTurnoColumns(t) {
  const col = (titleKey, key) => ({
    key,
    title: t(titleKey),
  });
  return [
    col('relOcupacaoTurno.headers.lineCode', 'cod_linha'),
    col('relOcupacaoTurno.headers.lineName', 'nome_linha'),
    col('relOcupacaoTurno.headers.shift', 'descricao_turno'),
    col('relOcupacaoTurno.headers.capacity', 'lotacao'),
    col('relOcupacaoTurno.headers.peopleCount', 'qtd_passageiros'),
    col('relOcupacaoTurno.headers.percent', 'taxa_ocupacao'),
    col('relOcupacaoTurno.headers.reservations', 'qtd_reservas'),
    col('relOcupacaoTurno.headers.predictedCount', 'qtd_ocupacao_prevista'),
    col('relOcupacaoTurno.headers.predictedPercent', 'taxa_ocupacao_prevista'),
  ];
}

/**
 * Colunas do status de comunicação dos veículos
 * (evita autoColumns com ids/lat/lng/ISO/flags numéricos da API).
 */
function statusVeiculosColumns(t) {
  const col = (titleKey, key) => ({
    key,
    title: t(titleKey),
  });
  return [
    col('relStatusVehicle.headers.company', 'razao_social'),
    col('relStatusVehicle.headers.line', 'cod_linha'),
    col('relStatusVehicle.headers.codeVehicle', 'cod_veiculo'),
    col('relStatusVehicle.headers.codePordataIntegretion', 'cod_integracao'),
    col('relStatusVehicle.headers.statusValidator', 'validador_offline_extenso'),
    col('relStatusVehicle.headers.codeQueclinkIntegretion', 'cod_integracao_queclink'),
    col('relStatusVehicle.headers.statusGps', 'gps_offline_extenso'),
    col('relStatusVehicle.headers.statusGeotab', 'status_geotab_extenso'),
    col('relStatusVehicle.headers.statusFaceid', 'status_faceid_extenso'),
    col('relStatusVehicle.headers.lastPositionDate', 'dt_posicao_extenso'),
    col('relStatusVehicle.headers.lastTicketingDate', 'dt_bilhetagem_extenso'),
    col('relStatusVehicle.headers.lastOdometerVirtualDate', 'dt_hodometro_virtual_extenso'),
    col('relStatusVehicle.headers.odometerVirtual', 'hodometro_virtual'),
    col('relStatusVehicle.headers.lastOdometerCanDate', 'dt_hodometro_canbus_extenso'),
    col('relStatusVehicle.headers.odometerCan', 'hodometro_canbus'),
  ];
}

export function computeLinhaIdsPorUnidade(linhasCatalog, unidades) {
  if (!Array.isArray(unidades) || unidades.length === 0) {
    return null;
  }
  const set = new Set(unidades.map((id) => Number(id)));
  return new Set(
    (linhasCatalog || [])
      .filter((l) => {
        const u = Array.isArray(l.unidades) ? l.unidades : [];
        return u.some((id) => set.has(Number(id)));
      })
      .map((l) => l.id_linha)
  );
}

/**
 * Igual à tela RELOperacao: se não houver linhas selecionadas, usa o catálogo
 * filtrado por empresa/unidades (a API exige id_linhas; null → AND 1=0).
 */
function resolveOperacaoLinhaIds(ctx) {
  if (Array.isArray(ctx.linhas) && ctx.linhas.length) {
    return ctx.linhas.map((id) => Number(id)).filter((id) => Number.isFinite(id));
  }
  let linhas = Array.isArray(ctx.linhasCatalog) ? ctx.linhasCatalog.slice() : [];
  if (ctx.empresa != null && ctx.empresa !== '') {
    const empresaId = Number(ctx.empresa);
    linhas = linhas.filter((l) => Number(l.id_empresa) === empresaId);
  }
  if (Array.isArray(ctx.unidades) && ctx.unidades.length) {
    const set = new Set(ctx.unidades.map((id) => Number(id)));
    linhas = linhas.filter((l) => {
      const u = Array.isArray(l.unidades) ? l.unidades : [];
      return u.some((id) => set.has(Number(id)));
    });
  }
  return linhas
    .map((l) => Number(l.id_linha))
    .filter((id) => Number.isFinite(id));
}

async function fetchLinhasAtivasCatalog(token) {
  try {
    const response = await apiController.request(apiController.endpoints.getLinhasativas, {
      silent: true,
      config: token ? { headers: { 'x-access-token': token } } : undefined,
    });
    return (response && response.data) || [];
  } catch (_err) {
    return [];
  }
}

/** getPrevistoRealizadoLinhasData exige id_linha; null → AND 1=0 na API. */
async function resolvePrevistoRealizadoLinhaIds(ctx) {
  let catalog = Array.isArray(ctx.linhasCatalog) ? ctx.linhasCatalog : [];
  if (!catalog.length) {
    catalog = await fetchLinhasAtivasCatalog(ctx.token);
  }
  return resolveOperacaoLinhaIds({ ...ctx, linhasCatalog: catalog });
}

function buildLinhaPostFilter(linhasSelecionadas, linhaIdsPorUnidade) {
  const a = linhasSelecionadas.length > 0 ? linhasSelecionadas : null;
  const b = linhaIdsPorUnidade ? Array.from(linhaIdsPorUnidade) : null;
  if (a && b) {
    const setB = new Set(b);
    return inFilter('id_linha', a.filter((id) => setB.has(id)));
  }
  if (a || b) {
    return inFilter('id_linha', a || b);
  }
  return {};
}

function buildPerfilPostFilter(empresa, user) {
  if (empresa) {
    return eqFilter('id_empresa', empresa);
  }
  if (user && user.id_cliente != null && user.id_cliente !== '') {
    return eqFilter('id_cliente', user.id_cliente);
  }
  return {};
}

function passaFiltroUnidadesPostFilters(unidades) {
  return inFilter('id_unidade', unidades);
}

function pontualidadeColumns(grupo, t) {
  const col = (key, value) => ({
    key: value,
    title: t(`relPontualidade.headers.${key}`),
  });
  const metricas = [col('trips', 'n_viagens'), col('punctuality', 'percentual_pontualidade')];
  if (grupo === 'veiculo') {
    return [col('vehicle', 'numero_ordem'), ...metricas];
  }
  if (grupo === 'linha') {
    return [col('lineCode', 'cod_linha'), col('lineName', 'nome_linha'), ...metricas];
  }
  if (grupo === 'unidade') {
    return [col('unit', 'descricao_unidade'), ...metricas];
  }
  if (grupo === 'empresa') {
    return [col('company', 'razao_social'), ...metricas];
  }
  return [col('client', 'nome_cliente'), ...metricas];
}

function passageirosLinhaDiaColumns(t) {
  return [
    { key: 'periodo_inicio_extenso', title: t('relPassageirosLinhaDia.headers.periodStart') },
    { key: 'periodo_fim_extenso', title: t('relPassageirosLinhaDia.headers.periodEnd') },
    { key: 'cod_linha', title: t('relPassageirosLinhaDia.headers.lineCode') },
    { key: 'nome_linha', title: t('relPassageirosLinhaDia.headers.lineName') },
    { key: 'qtd_embarques', title: t('relPassageirosLinhaDia.headers.boardings') },
  ];
}

function viagensCercaColumns(t) {
  return [
    colT(t, 'monitor.logbook.line', 'cod_linha'),
    colT(t, 'fields.vehicle', 'numero_ordem_veiculo_realizado'),
    colT(t, 'monitor.trip.departure', 'horario_saida_realizada_extenso'),
    colT(t, 'monitor.trip.arrival', 'horario_chegada_realizada_extenso'),
    colT(t, 'operation.direction', 'descricao_sentido'),
    colT(t, 'crudQuadros.headers.trips.shift', 'descricao_turno'),
    colT(t, 'relatoriosExtenso.headers.type', 'is_inicio'),
    colT(t, 'relatoriosExtenso.headers.manual', 'is_manual_extenso'),
  ];
}

function veiculosKmColumns(setup, t) {
  const columns = [
    { key: 'cod_veiculo', title: t('relVehicleKm.headersVeiculosKm.code') },
  ];
  if (setup && Number(setup.has_validador) === 1) {
    columns.push({
      key: 'cod_integracao',
      title: t('relStatusVehicle.headers.codePordataIntegretion'),
    });
  }
  if (setup && Number(setup.has_queclink) === 1) {
    columns.push({
      key: 'cod_integracao_queclink',
      title: t('relVehicleKm.headersVeiculosKm.equipment'),
    });
  }
  columns.push(
    { key: 'hodometro_can_inicio', title: t('relVehicleKm.headersVeiculosKm.canOdometerStart') },
    { key: 'dt_coleta_can_inicio', title: t('relVehicleKm.headersVeiculosKm.canDateStart') },
    { key: 'hodometro_can_fim', title: t('relVehicleKm.headersVeiculosKm.canOdometerEnd') },
    { key: 'dt_coleta_can_fim', title: t('relVehicleKm.headersVeiculosKm.canDateEnd') },
    { key: 'km_can', title: t('relVehicleKm.headersVeiculosKm.canKm') },
    { key: 'hodometro_virtual_inicio', title: t('relVehicleKm.headersVeiculosKm.virtualOdometerStart') },
    { key: 'dt_coleta_virtual_inicio', title: t('relVehicleKm.headersVeiculosKm.virtualDateStart') },
    { key: 'hodometro_virtual_fim', title: t('relVehicleKm.headersVeiculosKm.virtualOdometerEnd') },
    { key: 'dt_coleta_virtual_fim', title: t('relVehicleKm.headersVeiculosKm.virtualDateEnd') },
    { key: 'km_virtual', title: t('relVehicleKm.headersVeiculosKm.virtualKm') },
    { key: 'km_viagem', title: t('relVehicleKm.headersVeiculosKm.tripKm') },
    { key: 'km_ociosa', title: t('relVehicleKm.headersVeiculosKm.idleKm') }
  );
  return columns;
}

function baseCtxParams(ctx) {
  return buildRelatorioExtensoRequestParams(ctx.entry, {
    dtiSql: ctx.dtiSql,
    dtfSql: ctx.dtfSql,
    empresa: ctx.empresa,
    linhas: ctx.linhas,
    veiculos: ctx.veiculos,
    unidades: ctx.unidades,
    grupo: ctx.grupo,
    cerca: ctx.cerca,
    config: ctx.config,
  });
}

async function fetchSetup() {
  try {
    const response = await apiController.request(apiController.endpoints.getallParametrossetup, {
      silent: true,
    });
    const rows = (response && response.data) || [];
    return rows[0] || null;
  } catch (_err) {
    return null;
  }
}

function idsUnidadesNoVeiculo(o) {
  if (Array.isArray(o.unidades) && o.unidades.length) {
    return o.unidades.map((id) => Number(id));
  }
  if (o.id_unidade != null && o.id_unidade !== '') {
    return [Number(o.id_unidade)];
  }
  return [];
}

/** Espelha RELKmTempoReal.filtraItensLazyParaIds para export extenso sem veículos selecionados. */
function filterVeiculosPorEscopoExport(veiculos, ctx) {
  if (!Array.isArray(veiculos) || !veiculos.length) return [];
  let filtrados = veiculos;
  if (ctx.empresa) {
    filtrados = filtrados.filter((d) => d.id_empresa == ctx.empresa);
  } else if (ctx.user && ctx.user.id_cliente != null && ctx.user.id_cliente !== '') {
    filtrados = filtrados.filter((d) => d.id_cliente == ctx.user.id_cliente);
  }
  if (Array.isArray(ctx.unidades) && ctx.unidades.length > 0) {
    const selecionadas = ctx.unidades.map((id) => Number(id));
    filtrados = filtrados.filter((d) => {
      const rowUnidades = idsUnidadesNoVeiculo(d);
      if (rowUnidades.length > 0) {
        return rowUnidades.some((u) => selecionadas.includes(u));
      }
      return selecionadas.includes(-1);
    });
  }
  return filtrados;
}

async function fetchVeiculosAtivosCatalog(token) {
  try {
    const response = await apiController.request(apiController.endpoints.getallVeiculosativos, {
      silent: true,
      config: token ? { headers: { 'x-access-token': token } } : undefined,
    });
    return (response && response.data) || [];
  } catch (_err) {
    return [];
  }
}

/** Nenhum veículo selecionado = todos do escopo (empresa/cliente/unidade), como na tela. */
async function resolveKmTempoRealVeiculoIds(ctx) {
  if (Array.isArray(ctx.veiculos) && ctx.veiculos.length > 0) {
    return ctx.veiculos;
  }
  const catalog = await fetchVeiculosAtivosCatalog(ctx.token);
  return filterVeiculosPorEscopoExport(catalog, ctx)
    .map((v) => v.id_veiculo)
    .filter((id) => id != null && id !== '');
}

async function fetchManutencaoTiposEquipamento(token, tipoAba) {
  try {
    const response = await apiController.request(apiController.endpoints.getRelmanutencaotiposequipamentos, {
      silent: true,
      config: token ? { headers: { 'x-access-token': token } } : undefined,
    });
    const ids = (response && response.data && response.data[tipoAba]) || [];
    return Array.isArray(ids) ? ids : [];
  } catch (_err) {
    return [];
  }
}

/** @type {Record<string, (ctx: RelatorioExportContext) => Promise<object>|object>} */
const PROFILE_BUILDERS = {
  pontualidade(ctx) {
    const grupo = ctx.grupo || 'veiculo';
    return {
      filename: `pontualidade_${grupo}`,
      requestParams: baseCtxParams(ctx),
      columns: pontualidadeColumns(grupo, ctx.t),
      idField: 'id_agregado',
    };
  },

  atrasoViagens(ctx) {
    const linhaIds = computeLinhaIdsPorUnidade(ctx.linhasCatalog, ctx.unidades);
    return {
      requestParams: {
        dti: ctx.dtiSql,
        dtf: ctx.dtfSql,
        id_empresa: ctx.empresa || null,
        id_linhas: ctx.linhas.length ? ctx.linhas : null,
        id_veiculos: ctx.veiculos.length ? ctx.veiculos : null,
      },
      postFilters: mergePostFilters(
        buildLinhaPostFilter(ctx.linhas, linhaIds),
        inFilter('id_veiculo_realizado', ctx.veiculos)
      ),
      idField: 'id_viagem_grid',
      columns: atrasoViagensColumns(ctx.t),
    };
  },

  diarioBordo(ctx) {
    return {
      requestParams: {
        dti: ctx.dtiSql,
        dtf: ctx.dtfSql,
        idlinhas: ctx.linhas.length ? ctx.linhas : null,
        idveiculos: ctx.veiculos.length ? ctx.veiculos : null,
        idviagens: null,
      },
      postFilters: mergePostFilters(
        buildPerfilPostFilter(ctx.empresa, ctx.user),
        inFilter('id_unidade', ctx.unidades)
      ),
      columns: diarioBordoColumns(ctx.t),
    };
  },

  embarques(ctx) {
    const filters = {
      dti: ctx.dtiSql,
      dtf: ctx.dtfSql,
    };
    if (ctx.linhas.length) filters.id_linha = ctx.linhas.slice();
    if (ctx.veiculos.length) filters.id_veiculo = ctx.veiculos.slice();
    if (ctx.unidades.length) {
      filters.id_unidade = ctx.unidades.map((id) => Number(id));
    }
    return {
      filename: 'relembarques',
      filters,
      sortBy: ['dt_embarque_extenso'],
      sortDesc: [true],
      idField: 'id_embarque',
      columns: embarquesColumns(ctx.t),
    };
  },

  notificacoes(ctx) {
    return {
      requestParams: {
        dtinicio: ctx.dtiSql,
        dtfim: ctx.dtfSql,
        idempresa: ctx.empresa || null,
        idlinha: ctx.linhas.length === 1 ? ctx.linhas[0] : null,
        idveiculo: ctx.veiculos.length ? ctx.veiculos : null,
        tiponotificacao: null,
        tratada: null,
        bilhetagemfora: null,
      },
      postFilters: mergePostFilters(
        inFilter('id_linha', ctx.linhas.length > 1 ? ctx.linhas : []),
        passaFiltroUnidadesPostFilters(ctx.unidades)
      ),
      columns: notificacoesColumns(ctx.t),
    };
  },

  atrasoCercas(ctx) {
    return {
      filename: 'atrasosocupacaocercas',
      requestParams: { dti: ctx.dtiSql, dtf: ctx.dtfSql },
      postFilters: mergePostFilters(
        buildPerfilPostFilter(ctx.empresa, ctx.user),
        inFilter('id_linha', ctx.linhas),
        inFilter('id_veiculo', ctx.veiculos),
        inFilter('id_cerca', ctx.cerca ? [ctx.cerca] : []),
        passaFiltroUnidadesPostFilters(ctx.unidades)
      ),
      columns: atrasoCercasColumns(ctx.t),
    };
  },

  passageirosLinhaDia(ctx) {
    const requestParams = {
      dti: ctx.dtiSql,
      dtf: ctx.dtfSql,
      intervalo: 'DAY',
    };
    if (ctx.linhas.length) {
      requestParams.id_linhas = ctx.linhas.map((id) => Number(id));
    }
    if (ctx.unidades.length) {
      requestParams.id_unidades = ctx.unidades.map((id) => Number(id));
    }
    return {
      requestParams,
      columns: passageirosLinhaDiaColumns(ctx.t),
    };
  },

  tempoTrecho(ctx) {
    return {
      filename: 'tempodetrecho',
      requestParams: { dti: ctx.dtiSql, dtf: ctx.dtfSql },
      postFilters: mergePostFilters(
        inFilter('id_veiculo', ctx.veiculos),
        passaFiltroUnidadesPostFilters(ctx.unidades)
      ),
      columns: tempoTrechoColumns(ctx.t),
    };
  },

  viagensCerca(ctx) {
    return {
      requestParams: {
        dti: ctx.dtiSql,
        dtf: ctx.dtfSql,
        idcerca: ctx.cerca || null,
      },
      columns: viagensCercaColumns(ctx.t),
    };
  },

  velocidade(ctx) {
    return {
      filename: 'velocidade',
      requestParams: {
        dti: ctx.dtiSql,
        dtf: ctx.dtfSql,
        // null = sem filtro (API trata como todos; evita requestParams gigante)
        idlinhas: ctx.linhas.length ? ctx.linhas : null,
        idveiculos: ctx.veiculos.length ? ctx.veiculos : null,
        idcercas: null,
      },
      postFilters: passaFiltroUnidadesPostFilters(ctx.unidades),
      columns: velocidadeColumns(ctx.t),
    };
  },

  statusViagens(ctx) {
    return {
      requestParams: {
        dti: ctx.dtiSql,
        dtf: ctx.dtfSql,
        idlinhas: ctx.linhas.length ? ctx.linhas : null,
      },
      postFilters: passaFiltroUnidadesPostFilters(ctx.unidades),
      columns: statusViagensColumns(ctx.t),
    };
  },

  statusVeiculos(ctx) {
    return {
      filename: 'statusveiculos',
      requestParams: { dt: ctx.dtfSql || ctx.dtiSql },
      postFilters: mergePostFilters(
        buildPerfilPostFilter(ctx.empresa, ctx.user),
        inFilter('id_linha', ctx.linhas),
        inFilter('id_veiculo', ctx.veiculos),
        passaFiltroUnidadesPostFilters(ctx.unidades)
      ),
      columns: statusVeiculosColumns(ctx.t),
    };
  },

  cumprimentoRota(ctx) {
    return {
      title: ctx.t('routeCompliance.title'),
      filename: 'cumprimentorota',
      requestParams: {
        dti: ctx.dtiSql,
        dtf: ctx.dtfSql,
        // null = sem filtro (igual à tela quando a lista lazy não restringe)
        idlinhas: ctx.linhas.length ? ctx.linhas : null,
        idveiculos: ctx.veiculos.length ? ctx.veiculos : null,
        idfuncionarios: null,
        idempresa: ctx.empresa || null,
        idunidades: ctx.unidades.length ? ctx.unidades : [],
        idcliente:
          ctx.empresa
            ? null
            : (ctx.user && ctx.user.id_cliente != null && ctx.user.id_cliente !== ''
              ? ctx.user.id_cliente
              : null),
      },
      idField: 'id_viagem_grid',
      columns: cumprimentoRotaColumns(ctx.t),
    };
  },

  eventosHikvision(ctx) {
    return {
      requestParams: { dti: ctx.dtiSql, dtf: ctx.dtfSql },
      postFilters: mergePostFilters(
        inFilter('id_linha', ctx.linhas),
        inFilter('id_veiculo', ctx.veiculos),
        passaFiltroUnidadesPostFilters(ctx.unidades)
      ),
      columns: eventosHikvisionColumns(ctx.t),
    };
  },

  controladores(ctx) {
    return {
      requestParams: { dti: ctx.dtiSql, dtf: ctx.dtfSql },
      postFilters: mergePostFilters(
        inFilter('id_linha', ctx.linhas),
        inFilter('id_veiculo', ctx.veiculos),
        passaFiltroUnidadesPostFilters(ctx.unidades)
      ),
      columns: controladoresColumns(ctx.t),
    };
  },

  acessoQrCode(ctx) {
    // Status atual das pessoas: filtra quem teve janela de acesso e/ou download
    // intersectando [dti, dtf] (mesmo critério do intervalo de datas do relatório).
    const dateWindowFilters =
      ctx.dtiSql || ctx.dtfSql
        ? {
            from: ctx.dtiSql || null,
            to: ctx.dtfSql || null,
            windows: [
              ['primeiro_acesso_qrcode', 'ultimo_acesso_qrcode'],
              ['primeiro_download_qrcode', 'ultimo_download_qrcode'],
            ],
          }
        : null;
    return {
      columns: acessoQrCodeColumns(ctx.t),
      postFilters: passaFiltroUnidadesPostFilters(ctx.unidades),
      dateWindowFilters,
    };
  },

  historicoEnvioQrCode(ctx) {
    return {
      requestParams: { dti: ctx.dtiSql, dtf: ctx.dtfSql },
      postFilters: passaFiltroUnidadesPostFilters(ctx.unidades),
      columns: historicoEnvioQrCodeColumns(ctx.t),
    };
  },

  async operacao(ctx) {
    let catalog = Array.isArray(ctx.linhasCatalog) ? ctx.linhasCatalog : [];
    if (!catalog.length) {
      catalog = await fetchLinhasAtivasCatalog(ctx.token);
    }
    const id_linhas = resolveOperacaoLinhaIds({ ...ctx, linhasCatalog: catalog });
    return {
      requestParams: {
        dti: ctx.dtiSql,
        dtf: ctx.dtfSql,
        id_empresa: ctx.empresa || null,
        id_linhas: id_linhas.length ? id_linhas : null,
        id_veiculos: ctx.veiculos.length ? ctx.veiculos : null,
        id_unidades: ctx.unidades.length ? ctx.unidades : null,
      },
      columns: operacaoColumns(ctx.t),
    };
  },

  ocupacaoTurno(ctx) {
    const requestParams = {};
    if (ctx.linhas.length) {
      requestParams.id_linhas = ctx.linhas.map((id) => Number(id));
    }
    if (ctx.unidades.length) {
      requestParams.id_unidades = ctx.unidades.map((id) => Number(id));
    }
    return {
      filename: 'ocupacaoporturno',
      requestParams,
      columns: ocupacaoTurnoColumns(ctx.t),
    };
  },

  embarquesTrecho(ctx) {
    return {
      requestParams: { dti: ctx.dtiSql, dtf: ctx.dtfSql },
      columns: embarquesTrechoColumns(ctx.t),
    };
  },

  tempoPlacaServico(ctx) {
    return {
      requestParams: { dti: ctx.dtiSql, dtf: ctx.dtfSql },
      columns: tempoPlacaServicoColumns(ctx.t),
    };
  },

  tempoDePlaca(ctx) {
    return {
      requestParams: {
        dti: ctx.dtiSql,
        dtf: ctx.dtfSql,
        idlinhas: ctx.linhas.length ? ctx.linhas : null,
        idveiculos: ctx.veiculos.length ? ctx.veiculos : null,
      },
      columns: tempoDePlacaColumns(ctx.t),
    };
  },

  usuariosPassageiros(ctx) {
    return {
      filters: {
        dti: ctx.dtiSql,
        dtf: ctx.dtfSql,
      },
      sortBy: ['nome_completo'],
      sortDesc: [false],
      columns: usuariosPassageirosColumns(ctx.t),
    };
  },

  comboio(ctx) {
    return {
      title: ctx.t('convoyReport.report'),
      requestParams: {
        idlinha: ctx.linhas.length ? ctx.linhas[0] : null,
        dti: ctx.dtiSql,
        dtf: ctx.dtfSql,
      },
      postFilters: passaFiltroUnidadesPostFilters(ctx.unidades),
      columns: comboioColumns(ctx.t),
    };
  },

  consumoApiExterna(ctx) {
    return {
      requestParams: { dti: ctx.dtiSql, dtf: ctx.dtfSql },
      columns: consumoApiExternaColumns(ctx.t),
    };
  },

  historicoAlteracoesRotas(ctx) {
    const dateOnly = (v) => (v ? String(v).slice(0, 10) : null);
    return {
      title: ctx.t('routeChangeHistory.title'),
      requestParams: { dti: dateOnly(ctx.dtiSql), dtf: dateOnly(ctx.dtfSql) },
      columns: historicoAlteracoesRotasColumns(ctx.t),
    };
  },

  historicoEquipamentos(ctx) {
    return {
      title: ctx.t('relMaintenanceHistory.title'),
      requestParams: {
        ids: ctx.veiculos.length ? ctx.veiculos : [],
        dti: ctx.dtiSql,
        dtf: ctx.dtfSql,
      },
      postFilters: passaFiltroUnidadesPostFilters(ctx.unidades),
      columns: historicoEquipamentosColumns(ctx.t),
    };
  },

  historicoRoteirizacao(ctx) {
    const dateOnly = (v) => (v ? String(v).slice(0, 10) : null);
    return {
      title: ctx.t('routingHistoryTimeline.title'),
      requestParams: { dti: dateOnly(ctx.dtiSql), dtf: dateOnly(ctx.dtfSql) },
      columns: historicoRoteirizacaoColumns(ctx.t),
    };
  },

  historicoVeiculo(ctx) {
    const variant = getRelatorioExtensoTableVariant(ctx.entry, ctx.tableVariant);
    const variantId = variant ? variant.id : 'viagens';
    const tableLabel = variant ? ctx.t(variant.labelKey) : ctx.t('RELHistoricoVeiculo.tabs.realizedTrips');
    return {
      title: `${ctx.t('RELHistoricoVeiculo.title')} - ${tableLabel}`,
      listEndpoint: variant ? variant.listEndpoint : ctx.entry.listEndpoint,
      codTabela: variant ? variant.codTabela : ctx.entry.codTabela,
      filename: variant ? variant.filename : ctx.entry.filename,
      requestParams: historicoVeiculoRequestParams(variantId, ctx),
      columns: historicoVeiculoColumnsByVariant(ctx.t, variantId),
    };
  },

  async kmTempoReal(ctx) {
    const ids = await resolveKmTempoRealVeiculoIds(ctx);
    return {
      title: ctx.t('realTimeKmReport.title'),
      requestParams: { ids },
      postFilters: passaFiltroUnidadesPostFilters(ctx.unidades),
      columns: kmTempoRealColumns(ctx.t),
      subtitle: '',
    };
  },

  async manutencaoEquipamentos(ctx) {
    const variant = getRelatorioExtensoTableVariant(ctx.entry, ctx.tableVariant);
    const variantId = variant ? variant.id : 'rastreadores';
    const tipoAba = (variant && variant.tipoAba) || variantId;
    const tableLabel = variant ? ctx.t(variant.labelKey) : ctx.t('relatoriosExtenso.manutencao.tabs.rastreadores');
    const idsTiposEquipamento = await fetchManutencaoTiposEquipamento(ctx.token, tipoAba);
    const isComunicacao = variant && variant.tipo === 'comunicacao';

    if (isComunicacao) {
      return {
        title: `${ctx.t('nav.reports.equipMaintenance')} - ${tableLabel}`,
        listEndpoint: variant.listEndpoint,
        filename: variant.filename,
        requestParams: { idsTiposEquipamento },
        postFilters: mergePostFilters(
          buildPerfilPostFilter(ctx.empresa, ctx.user),
          passaFiltroUnidadesPostFilters(ctx.unidades),
          inFilter('id_veiculo', ctx.veiculos),
        ),
        columns: manutencaoComunicacaoColumns(ctx.t),
      };
    }

    const ids = await resolveKmTempoRealVeiculoIds(ctx);
    return {
      title: `${ctx.t('nav.reports.equipMaintenance')} - ${tableLabel}`,
      listEndpoint: variant ? variant.listEndpoint : ctx.entry.listEndpoint,
      filename: variant ? variant.filename : ctx.entry.filename,
      requestParams: {
        ids,
        dti: ctx.dtiSql,
        dtf: ctx.dtfSql,
        param: 'veiculo',
        idsTiposEquipamento,
      },
      postFilters: passaFiltroUnidadesPostFilters(ctx.unidades),
      columns: manutencaoMetricasColumns(ctx.t, variantId),
    };
  },

  async passageirosViagem(ctx) {
    const variant = getRelatorioExtensoTableVariant(ctx.entry, ctx.tableVariant);
    const variantId = variant ? variant.id : 'faixas';
    const tableLabel = variant ? ctx.t(variant.labelKey) : ctx.t('relTripPassangers.title');

    if (variantId === 'viagem') {
      const idLinhas = await resolvePrevistoRealizadoLinhaIds(ctx);
      return {
        title: `${ctx.t('nav.reports.passengers')} - ${tableLabel}`,
        listEndpoint: variant.listEndpoint,
        codTabela: variant.codTabela,
        filename: variant.filename,
        requestParams: {
          id_linha: idLinhas.length ? idLinhas : null,
          dti: ctx.dtiSql,
          dtf: ctx.dtfSql,
        },
        postFilters: mergePostFilters(
          passaFiltroUnidadesPostFilters(ctx.unidades),
          inFilter('id_veiculo', ctx.veiculos),
          notNullFilter('passageiros_totais'),
        ),
        columns: passageirosViagemTripColumns(ctx.t),
      };
    }

    return {
      title: `${ctx.t('nav.reports.passengers')} - ${tableLabel}`,
      listEndpoint: variant ? variant.listEndpoint : ctx.entry.listEndpoint,
      codTabela: variant ? variant.codTabela : ctx.entry.codTabela,
      filename: variant ? variant.filename : ctx.entry.filename,
      requestParams: {
        dti: ctx.dtiSql,
        dtf: ctx.dtfSql,
        intervalo: ctx.intervalo || 'DAY',
        idlinhas: ctx.linhas.length ? ctx.linhas : null,
        idveiculos: ctx.veiculos.length ? ctx.veiculos : null,
      },
      postFilters: passaFiltroUnidadesPostFilters(ctx.unidades),
      columns: passageirosFaixasColumns(ctx.t),
    };
  },

  async previstoRealizado(ctx) {
    const idLinhas = await resolvePrevistoRealizadoLinhaIds(ctx);
    const hora = ctx.config && ctx.config.hora_inicial_dia_operacao
      ? String(ctx.config.hora_inicial_dia_operacao).replace(/:/g, '')
      : null;
    return {
      title: ctx.t('relPredictedRealized.title'),
      requestParams: {
        id_linha: idLinhas.length ? idLinhas : null,
        id_veiculo: ctx.veiculos.length ? ctx.veiculos : null,
        dti: ctx.dtiSql,
        dtf: ctx.dtfSql,
        inicio_operacional: hora ? `${hora}00` : null,
      },
      postFilters: mergePostFilters(
        passaFiltroUnidadesPostFilters(ctx.unidades),
        inFilter('id_veiculo', ctx.veiculos),
      ),
      columns: previstoRealizadoColumns(ctx.t),
    };
  },
};

/**
 * @param {RelatorioExtensoEntry} entry
 * @param {Partial<RelatorioExportContext>} rawCtx
 * @returns {Promise<object>}
 */
export async function buildRelatorioExportPayload(entry, rawCtx) {
  /** @type {RelatorioExportContext} */
  const ctx = {
    entry,
    dtiSql: rawCtx.dtiSql || null,
    dtfSql: rawCtx.dtfSql || null,
    empresa: rawCtx.empresa ?? null,
    linhas: Array.isArray(rawCtx.linhas) ? rawCtx.linhas : [],
    veiculos: Array.isArray(rawCtx.veiculos) ? rawCtx.veiculos : [],
    unidades: Array.isArray(rawCtx.unidades) ? rawCtx.unidades : [],
    grupo: rawCtx.grupo || 'veiculo',
    cerca: rawCtx.cerca ?? null,
    format: rawCtx.format || 'csv',
    token: rawCtx.token || sessionStorage.getItem('api'),
    user: rawCtx.user || null,
    linhasCatalog: Array.isArray(rawCtx.linhasCatalog) ? rawCtx.linhasCatalog : [],
    setup: rawCtx.setup || null,
    config: rawCtx.config || null,
    tableVariant: rawCtx.tableVariant || null,
    intervalo: rawCtx.intervalo || null,
    t: rawCtx.t || ((key) => key),
  };

  const title = ctx.t(entry.labelKey);
  let subtitle = formatExportSubtitle(ctx.dtiSql, ctx.dtfSql);

  const builder = PROFILE_BUILDERS[entry.id];
  const profile = builder ? await Promise.resolve(builder(ctx)) : {};

  let requestParams = profile.requestParams;
  if (requestParams == null) {
    requestParams = baseCtxParams(ctx);
  }

  const effectiveCodTabela = profile.codTabela || entry.codTabela;

  let columns = profile.columns;
  if (!columns && effectiveCodTabela) {
    columns = await resolveTabelaFrontExportColumns(effectiveCodTabela, ctx.token);
  }
  if (!columns && Array.isArray(entry.columns) && entry.columns.length) {
    columns = entry.columns.map((c) => ({
      key: c.key,
      title: c.titleKey ? ctx.t(c.titleKey) : (c.title || c.key),
    }));
  }
  if (!columns && entry.id === 'veiculosKm') {
    const setup = ctx.setup || (await fetchSetup());
    columns = veiculosKmColumns(setup, ctx.t);
    const empresaRow = (rawCtx.empresas || []).find((e) => e.id_empresa === ctx.empresa);
    const razao = empresaRow ? empresaRow.razao_social : ctx.t('company.allTheCompanies');
    subtitle = `${razao} | ${ctx.t('relVehicleKm.refDate')} : ${formatExportSubtitle(ctx.dtiSql, ctx.dtfSql)}`;
  }

  const useAutoColumns = !columns || !columns.length;
  if (useAutoColumns && !entry.autoColumns) {
    // Sem headers no banco e sem fallback: worker deriva colunas das linhas.
    columns = [];
  }

  const payload = {
    filename: profile.filename || entry.filename,
    title: profile.title || title,
    subtitle: profile.subtitle != null ? profile.subtitle : subtitle,
    listEndpoint: profile.listEndpoint || entry.listEndpoint,
    format: ctx.format,
    columns,
    autoColumns: useAutoColumns || !!entry.autoColumns,
  };

  if (requestParams && Object.keys(requestParams).length > 0) {
    payload.requestParams = requestParams;
  }
  if (profile.postFilters && Object.keys(profile.postFilters).length > 0) {
    payload.postFilters = profile.postFilters;
  }
  if (profile.dateWindowFilters) {
    payload.dateWindowFilters = profile.dateWindowFilters;
  }
  if (profile.filters && Object.keys(profile.filters).length > 0) {
    payload.filters = profile.filters;
  }
  if (Array.isArray(profile.sortBy) && profile.sortBy.length) {
    payload.sortBy = profile.sortBy;
    payload.sortDesc = Array.isArray(profile.sortDesc)
      ? profile.sortDesc.map((d) => d === true || d === 'true')
      : [];
  }
  if (profile.idField) {
    payload.idField = profile.idField;
  }

  return payload;
}
